import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SetInfoHelper from "@/components/track/manual/SetInfoHelper";
import ManualEntryForm from "@/components/track/manual/Form";
import { useMutation } from "@tanstack/react-query";
import { useToastHandler } from "@/hooks/useToastHandler";
import { setApi } from "@/api/setApi";
import PageTitleScript from "@/components/MetaScript";
import { AnimatePresence, motion } from "motion/react";
import { useRefetchUserInfo } from "@/hooks/useUserInfo";
import { useInvalidateResources } from "@/hooks/useInvalidateResources";
import { WeightInfoModal } from "@/components/track/manual/WeightInfoModal";
import { useOneTimeHelper } from "@/hooks/useOneTimeHelper";
import { AxiosError } from "axios";
import { useValidationErrors } from "@/hooks/useValidationErrors";

type ExerciseFormData = {
  exerciseNanoId: string;
  exerciseName: string;
  reps: number;
  weight: number;
  unit: "lb" | "kg";
};

export function ManualTrackPage() {
  const navigate = useNavigate();

  const invalidate = useInvalidateResources();
  const refetchUserInfo = useRefetchUserInfo();

  const { showHelper, dismissHelper } = useOneTimeHelper("set-info");

  const [showTrainingLoadModal, setShowTrainingLoadModal] = useState(false);
  const { validationErrors, removeValidationError, clearAllValidationErrors, setValidationErrorsFromResponse } = useValidationErrors();
  const { handleError, handleSuccess, clearToasts } = useToastHandler();
  const [formData, setFormData] = useState<ExerciseFormData>({
    exerciseNanoId: "",
    exerciseName: "",
    reps: 10,
    weight: 0,
    unit: "lb",
  });

  // Handle exercise selection or creation
  const handleExerciseChange = (id: number | string, label: string) => {
    setFormData((prev) => ({
      ...prev,
      exerciseNanoId: id.toString(),
      exerciseName: label,
    }));
  };

  // Add a new workout set
  const { mutate: submitForm, isPending: isSubmitting } = useMutation({
    mutationFn: async () => {
      if (!formData.exerciseNanoId && !formData.exerciseName) {
        throw new Error("Please select or create an exercise");
      }

      // Clear any previous toasts
      clearToasts();

      // Use a single API call for both cases
      const addedSetResource = await setApi.trackNewSetByText(
        formData.exerciseNanoId,
        formData.exerciseName,
        formData.weight,
        formData.reps,
        formData.unit
      );

      return addedSetResource;
    },
    onSuccess: (addedSetResource) => {
      invalidate([
        "/api/sets/recent",
        "/api/exercises/tracked",
        "/api/workouts",
        "/api/stats/overview",
      ]);
      refetchUserInfo();

      // Clear validation errors on success
      clearAllValidationErrors();
      handleSuccess("New set saved successfully!", 1000);
      navigate("/workouts/date/" + addedSetResource.claimed_train_local_date);
    },
    onError: (error) => {
      if (
        error instanceof AxiosError &&
        error.response?.data?.validation_errors
      ) {
        // Store validation errors to display under inputs
        setValidationErrorsFromResponse(error.response.data.validation_errors);
        //return; // Don't show generic error toast for validation errors
      }
      handleError(error);
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "weight" || name === "reps") {
      // Convert to number, handle empty input as 0
      const numValue = value === "" ? 0 : Number(value);

      // Only update if it's a valid number
      if (!isNaN(numValue)) {
        setFormData((prev) => ({
          ...prev,
          [name]: numValue,
        }));
      }
    } else {
      // Handle non-numeric inputs
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitForm();
  };

  return (
    <div className="flex flex-col">
      <PageTitleScript title="Manually Track" />
      {/* Header */}
      <div className="w-full px-6 pt-4 pb-4 flex justify-between items-center">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 mr-2"
            onClick={() => navigate("/track")}
          >
            <ArrowLeft className="h-6 w-6 text-tx-dark" />
          </Button>
          <h1 className="text-2xl font-bold text-tx-dark">Enter a New Set</h1>
        </div>
      </div>

      {/* Form */}
      <div className="w-full px-6 pb-20">
        <Card className="rounded-xl overflow-hidden shadow-md pt-8 pb-8 bg-white border border-bd-light">
          {/* Helper information with close button */}
          <AnimatePresence mode="popLayout">
            {showHelper && (
              <motion.li
                className="block list-none p-0 m-0"
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                key="helper"
                transition={{
                  duration: 0.4,
                  scale: { type: "spring", visualDuration: 0.4, bounce: 0.1 },
                }}
              >
                <SetInfoHelper onClose={dismissHelper} />
              </motion.li>
            )}

            <motion.li
              className="block list-none p-0 m-0"
              layout
              transition={{
                duration: 0.2,
                scale: { type: "spring", visualDuration: 0.2, bounce: 0.1 },
              }}
              key="form"
            >
              <CardContent className="pb-5">
                <ManualEntryForm
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleExerciseChange={handleExerciseChange}
                  handleSubmit={handleSubmit}
                  setShowTrainingLoadModal={setShowTrainingLoadModal}
                  isSubmitting={isSubmitting}
                  validationErrors={validationErrors}
                  removeValidationError={removeValidationError}
                />
              </CardContent>
            </motion.li>
          </AnimatePresence>
        </Card>

        {/* Weight Helper Modal */}
        <WeightInfoModal
          open={showTrainingLoadModal}
          onClose={() => setShowTrainingLoadModal(false)}
        />

        {/* a link of Use voice instead to go back to the track page*/}
        <div className="flex justify-center mt-4">
          <Button
            variant="link"
            className="w-full py-3 underline"
            onClick={() => navigate("/track")}
          >
            Use Voice Input
          </Button>
        </div>
      </div>
    </div>
  );
}
